<template>
  <div class="my-score-page">
    <div class="score-hero">
      <van-nav-bar
        title="我的积分"
        left-arrow
        :border="false"
        @click-left="onClickLeft"
      />
      <div class="hero-copy">
        <div class="hero-title">积分成长看板</div>
        <div class="hero-desc">同步查看当前排名、推荐会员男女占比与已入会会员</div>
      </div>
    </div>

    <div class="dashboard-card score-card">
      <div class="dashboard-title">
        <span>🏆 我的积分</span>
        <span class="title-extra">{{ rankText }}</span>
      </div>
      <div class="point-display">
        <div class="point-number">{{ totalPoints }}</div>
        <div class="point-rank">总积分</div>
      </div>
    </div>

    <div class="dashboard-card ratio-card">
      <div class="dashboard-title">
        <span>👥 推荐会员 · 男女占比</span>
        <span class="title-extra">共 {{ referredTotal }} 人推荐</span>
      </div>
      <div class="ratio-bar-wrap">
        <div class="ratio-bar">
          <div
            class="ratio-fill male-fill"
            :style="{ width: malePercent }"
          >
            <span v-if="malePercent !== '0%'" class="ratio-label">♂ {{ malePercent }}</span>
          </div>
          <div
            class="ratio-fill female-fill"
            :style="{ width: femalePercent }"
          >
            <span v-if="femalePercent !== '0%'" class="ratio-label">♀ {{ femalePercent }}</span>
          </div>
        </div>
        <div class="ratio-legend">
          <div class="legend-item legend-male">
            <span class="legend-dot male-dot"></span>
            <span class="legend-text">男会员 {{ referredMaleCount }} 人</span>
          </div>
          <div class="legend-item legend-female">
            <span class="legend-dot female-dot"></span>
            <span class="legend-text">女会员 {{ referredFemaleCount }} 人</span>
          </div>
        </div>
      </div>
    </div>

    <div class="dashboard-card members-card">
      <div class="dashboard-title">
        <span>👥 入会会员</span>
        <span class="title-extra">已加载 {{ memberList.length }} 人</span>
      </div>

      <van-list
        v-model="loading"
        :finished="finished"
        finished-text="没有更多了"
        @load="loadMemberList"
      >
        <div class="member-list" v-if="memberList.length > 0">
          <div
            v-for="item in memberList"
            :key="item.id"
            class="member-item"
          >
            <div class="member-avatar">
              <img
                :src="item.avatarLink || defaultPhoto"
                alt=""
                @error="handleImageError($event)"
              />
            </div>
            <div class="member-main">
              <div class="member-name">{{ item.name || '未命名会员' }}</div>
              <div class="member-company">{{ item.companyName || '外部会员' }}</div>
            </div>
            <div class="member-side">
              <div class="member-date">{{ formatTime(item.joiningTime) || '--' }}</div>
              <div class="member-tag">已入会</div>
            </div>
          </div>
        </div>
      </van-list>

      <div v-if="!loading && memberList.length === 0" class="empty-panel">
        <van-empty description="暂无入会会员" />
      </div>
    </div>
  </div>
</template>

<script>
import { Toast } from 'vant';
import { getMyScoreInfo } from '@/api/matchmakerInfo';

export default {
  data() {
    return {
      totalPoints: 0,
      rank: 0,
      totalCount: 0,
      referredMaleCount: 0,
      referredFemaleCount: 0,
      memberList: [],
      loading: false,
      finished: false,
      page: 1,
      pageSize: 20,
      defaultPhoto: require('../assets/defaultPhoto.png')
    };
  },
  computed: {
    baseInfo() {
      return this.$store.getters.baseInfo;
    },
    rankText() {
      if (!this.totalCount) {
        return '排名待更新';
      }
      return `第${this.rank || 0}名 / 共${this.totalCount}位红娘`;
    },
    referredTotal() {
      return (this.referredMaleCount || 0) + (this.referredFemaleCount || 0);
    },
    malePercent() {
      const total = this.referredTotal;
      if (total === 0) return '0%';
      return `${Math.round((this.referredMaleCount / total) * 100)}%`;
    },
    femalePercent() {
      const total = this.referredTotal;
      if (total === 0) return '0%';
      return `${Math.round((this.referredFemaleCount / total) * 100)}%`;
    }
  },
  methods: {
    onClickLeft() {
      this.$router.go(-1);
    },
    handleImageError(event) {
      event.target.src = this.defaultPhoto;
    },
    formatTime(time) {
      if (!time) return '';
      const date = new Date(time);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    },
    async loadMyScore() {
      if (!this.baseInfo || !this.baseInfo.id) {
        Toast.fail({ message: '用户信息错误' });
        return;
      }

      try {
        const res = await getMyScoreInfo(this.baseInfo.id);
        if (res.code === 20000 && res.data) {
          this.totalPoints = res.data.totalPoints || 0;
          this.rank = res.data.rank || 0;
          this.totalCount = res.data.totalCount || 0;
          this.referredMaleCount = res.data.referredMaleCount || 0;
          this.referredFemaleCount = res.data.referredFemaleCount || 0;
        } else {
          Toast.fail({ message: res.msg || '加载失败' });
        }
      } catch (error) {
        Toast.fail({ message: '加载失败' });
      }
    },
    async loadMemberList() {
      if (!this.baseInfo || !this.baseInfo.id) {
        this.finished = true;
        return;
      }

      try {
        const res = await getMyScoreInfo(this.baseInfo.id, this.page, this.pageSize);
        if (res.code === 20000 && res.data && res.data.memberList) {
          const newList = res.data.memberList;
          this.memberList = [...this.memberList, ...newList];
          this.page++;
          if (newList.length < this.pageSize) {
            this.finished = true;
          }
        } else {
          this.finished = true;
        }
      } catch (error) {
        Toast.fail({ message: '加载失败' });
        this.finished = true;
      } finally {
        this.loading = false;
      }
    }
  },
  created() {
    this.loadMyScore();
  }
};
</script>

<style lang="less" scoped>
@import url('../style/theme-variables.less');
@import url('../style/prototype-common.less');

.my-score-page {
  .prototype-page-shell();
  min-height: 100vh;
  padding-bottom: 1.4rem;
}

.score-hero {
  padding: 0.35rem 0 1rem;
  background: linear-gradient(180deg, #ffffff 0%, #fff0f3 100%);

  /deep/ .van-nav-bar {
    background: transparent;

    .van-nav-bar__title,
    .van-icon {
      color: @text-primary;
    }
  }

  .hero-copy {
    padding: 0.2rem 0.8rem 0;
  }

  .hero-title {
    font-size: 1.18rem;
    line-height: 1.48rem;
    font-weight: @font-weight-bold;
    color: @text-primary;
  }

  .hero-desc {
    margin-top: 0.22rem;
    font-size: 0.58rem;
    line-height: 0.84rem;
    color: @text-tertiary;
  }
}

.dashboard-card {
  .prototype-card();
  margin: 0 0.8rem 0.8rem;
  padding: 1rem;
}

.dashboard-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.82rem;
  line-height: 1.12rem;
  font-weight: @font-weight-bold;
  color: @text-primary;
}

.title-extra {
  font-size: 0.54rem;
  line-height: 0.78rem;
  color: @text-tertiary;
  font-weight: @font-weight-medium;
}

.point-display {
  text-align: center;
  padding: 1.1rem 0 0.9rem;
}

.point-number {
  font-size: 2.2rem;
  line-height: 2.4rem;
  font-weight: @font-weight-bold;
  color: @primary-color;
}

.point-rank {
  margin-top: 0.18rem;
  font-size: 0.62rem;
  line-height: 0.88rem;
  color: @text-tertiary;
}

.ratio-bar-wrap {
  margin-top: 0.9rem;
}

.ratio-bar {
  display: flex;
  height: 2rem;
  border-radius: 0.6rem;
  overflow: hidden;
  background: @bg-gray;
}

.ratio-fill {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  transition: width 0.6s ease;
}

.male-fill {
  background: linear-gradient(135deg, #E8B896 0%, #D49C72 100%);
}

.female-fill {
  background: @gradient-primary;
}

.ratio-label {
  font-size: 0.64rem;
  font-weight: @font-weight-bold;
  color: #fff;
  white-space: nowrap;
}

.ratio-legend {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.2rem;
  margin-top: 0.7rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.32rem;
}

.legend-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  flex-shrink: 0;
}

.male-dot {
  background: #D49C72;
}

.female-dot {
  background: @primary-color;
}

.legend-text {
  font-size: 0.6rem;
  line-height: 0.84rem;
  color: @text-secondary;
  font-weight: @font-weight-medium;
}

.members-card {
  margin-bottom: 0;
}

.member-list {
  margin-top: 0.9rem;
}

.member-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.9rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
}

.member-avatar {
  width: 2.65rem;
  height: 2.65rem;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  box-shadow: @shadow-avatar;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.member-main {
  min-width: 0;
  flex: 1;
}

.member-name {
  font-size: 0.72rem;
  line-height: 1rem;
  font-weight: @font-weight-semibold;
  color: @text-primary;
}

.member-company {
  margin-top: 0.18rem;
  font-size: 0.56rem;
  line-height: 0.8rem;
  color: @text-secondary;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.member-side {
  text-align: right;
  flex-shrink: 0;
}

.member-date {
  font-size: 0.54rem;
  line-height: 0.76rem;
  color: @text-tertiary;
}

.member-tag {
  margin-top: 0.22rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2.35rem;
  height: 0.94rem;
  padding: 0 0.35rem;
  border-radius: 999px;
  background: rgba(255, 138, 154, 0.12);
  font-size: 0.5rem;
  color: @primary-color;
}

.empty-panel {
  .prototype-empty-panel();
  margin-top: 0.8rem;
}
</style>



// WEBPACK FOOTER //
// src/views/myScore.vue